import React from 'react';
import { Card, Text, Button, cn } from '@chatsdet/ui';
import { Clock, ArrowsClockwise } from '@phosphor-icons/react';
import { useAuth } from '../../../contexts/AuthContext';
import { useRunScheduleNow } from '../../../features/scheduling';
import {
  useSuiteSchedules,
  describeScheduleTarget,
  type TestSuiteSchedule,
} from '../../../features/test-suites';
import { MATCH_LOCKUP_BORDER } from './test-runs/matchLockupBorder';

const WINDOW_MS = 72 * 60 * 60 * 1000;

/** Concise "in 3h" / "in 2d" / "in 12m" label for a near-future timestamp. */
function formatRelativeFuture(iso: string): string {
  const ms = new Date(iso).getTime() - Date.now();
  if (Number.isNaN(ms)) return '';
  if (ms <= 0) return 'now';
  const minutes = Math.round(ms / 60_000);
  if (minutes < 60) return `in ${minutes}m`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `in ${hours}h`;
  return `in ${Math.round(hours / 24)}d`;
}

function UpcomingRow({
  schedule,
  onRunTriggered,
}: {
  schedule: TestSuiteSchedule;
  onRunTriggered?: () => void;
}) {
  const runNowMutation = useRunScheduleNow();
  // Spin the icon on click for clear feedback. The POST returns in ~200ms, so
  // hold a brief minimum spin (which overlaps the live-section refresh burst).
  const [spinning, setSpinning] = React.useState(false);
  React.useEffect(() => {
    if (!spinning) return;
    const t = window.setTimeout(() => setSpinning(false), 1200);
    return () => window.clearTimeout(t);
  }, [spinning]);

  const handleRunNow = () => {
    setSpinning(true);
    runNowMutation.mutate(schedule.ruleId, {
      onSuccess: () => onRunTriggered?.(),
    });
  };

  const isSpinning = spinning || runNowMutation.isPending;
  return (
    <div className="flex items-center justify-between gap-3 py-2 px-3 border-b border-border last:border-b-0">
      <div className="flex min-w-0 items-center gap-2">
        <Clock size={15} className="shrink-0 text-muted-foreground" />
        <div className="min-w-0">
          <Text size="sm" weight="medium" className="truncate">
            {describeScheduleTarget(schedule)}
          </Text>
          <Text size="xs" className="text-muted-foreground truncate">
            {schedule.description}
          </Text>
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <Text size="sm" className="text-muted-foreground">
          {schedule.nextRunAt ? formatRelativeFuture(schedule.nextRunAt) : '—'}
        </Text>
        <Button
          variant="icon"
          size="sm"
          iconOnly
          tooltip="Run now"
          iconLeft={<ArrowsClockwise size={15} className={isSpinning ? 'animate-spin' : undefined} />}
          onPress={handleRunNow}
          isPending={runNowMutation.isPending}
        />
      </div>
    </div>
  );
}

/**
 * Data half of the "Upcoming" rail section: the next run of every enabled
 * schedule due within the next 72 hours, soonest first. Split out from the
 * component so `RunsRail` can decide up front whether the rail has anything
 * to show at all (Live empty + Upcoming empty ⇒ no rail, not an empty column)
 * without duplicating the schedule-fetch and window-filter logic.
 */
export function useUpcomingScheduledRuns(): TestSuiteSchedule[] {
  const { user } = useAuth();
  const suitesEnabled = Boolean(user?.features?.suiteBuilderV2);
  const { data } = useSuiteSchedules(undefined, suitesEnabled);

  // Candidate schedules that could ever land in the window below — used only
  // to decide whether the minute tick (below) has anything to tick for.
  const hasCandidates = suitesEnabled && Boolean(data?.schedules?.some((s) => s.enabled && s.nextRunAt));

  // The filter/map/sort below reads `Date.now()`, so it can't be memoized on
  // `data` alone without freezing every "in 23h" label at whatever moment
  // `data` last changed (schedules aren't refetched on any regular interval).
  // Recomputing once a minute — via this bucketed tick, a memo dependency
  // rather than `Date.now()` read directly in the body — keeps the window
  // filter and the labels accurate without re-deriving the list on every
  // render of the page above (e.g. every filter-bar keystroke). Only runs
  // when there's a schedule that could ever appear in the window — otherwise
  // this would tick forever with nothing to show for it (suite feature off,
  // or no enabled schedules at all).
  const [nowMinute, setNowMinute] = React.useState(() => Math.floor(Date.now() / 60_000));
  React.useEffect(() => {
    if (!hasCandidates) return;
    const id = window.setInterval(() => {
      setNowMinute(Math.floor(Date.now() / 60_000));
    }, 60_000);
    return () => window.clearInterval(id);
  }, [hasCandidates]);

  const upcoming = React.useMemo(() => {
    if (!suitesEnabled) return [];
    const now = nowMinute * 60_000;
    return (data?.schedules ?? [])
      .filter((s) => s.enabled && s.nextRunAt)
      .map((s) => ({ s, at: new Date(s.nextRunAt as string).getTime() }))
      .filter(({ at }) => !Number.isNaN(at) && at >= now && at <= now + WINDOW_MS)
      .sort((a, b) => a.at - b.at)
      .map(({ s }) => s);
  }, [suitesEnabled, data, nowMinute]);

  return upcoming;
}

/**
 * "Upcoming" rail section: rows for the schedules `useUpcomingScheduledRuns`
 * resolved, target name + description + "in 23h" + a "Run now" icon button.
 * Sits in its own `Card` — the same primitive (same border/radius/background
 * tokens, see `MATCH_LOCKUP_BORDER`) `LiveRunCard` uses — so the rail reads
 * as two cards under two quiet headings, not one styled card next to an
 * unstyled list. `padding` stays `none` (each row already owns its own
 * `px-3 py-2`) and `gap-y-0` overrides `Card`'s default row gap so rows sit
 * flush, separated only by their own `border-b`; `overflow-hidden` clips the
 * first/last row's corners to the card's radius.
 */
export function UpcomingScheduledRuns({
  upcoming,
  onRunTriggered,
}: {
  upcoming: TestSuiteSchedule[];
  onRunTriggered?: () => void;
}) {
  return (
    <div>
      <Text
        size="xs"
        weight="semibold"
        className="mb-2 text-muted-foreground uppercase tracking-wide"
      >
        Upcoming
      </Text>
      <Card padding="none" className={cn('overflow-hidden gap-y-0', MATCH_LOCKUP_BORDER)}>
        {upcoming.map((s) => (
          <UpcomingRow key={s.ruleId} schedule={s} onRunTriggered={onRunTriggered} />
        ))}
      </Card>
    </div>
  );
}
